import type {
  POResponse,
  POListResponse,
  PONumberResponse,
  CreatePORequest,
  UpdatePORequest,
  UpdateStatusRequest,
  UpdateItemStatusRequest,
  POFilter,
  Vendor,
  POStatus,
} from '../types/purchaseorder';
import { get, post, put, patch, del, buildUrl } from './apiClient';

const API_BASE = '/api/v1/po';

// ============================================================================
// Purchase Order CRUD
// ============================================================================

export async function createPO(request: CreatePORequest): Promise<POResponse> {
  return post<POResponse>(API_BASE, request);
}

export async function getPO(id: string): Promise<POResponse> {
  return get<POResponse>(`${API_BASE}/${id}`);
}

export async function listPOs(filter?: POFilter): Promise<POListResponse> {
  return get<POListResponse>(buildUrl(API_BASE, filter));
}

export async function updatePO(id: string, request: UpdatePORequest): Promise<POResponse> {
  return put<POResponse>(`${API_BASE}/${id}`, request);
}

export async function updatePOStatus(id: string, status: POStatus): Promise<void> {
  await patch(`${API_BASE}/${id}/status`, { status } as UpdateStatusRequest);
}

export async function updateItemStatus(
  poId: string,
  lineNo: number,
  request: Omit<UpdateItemStatusRequest, 'line_no'>
): Promise<void> {
  await patch(`${API_BASE}/${poId}/items/${lineNo}/status`, request);
}

export async function deletePO(id: string): Promise<void> {
  await del(`${API_BASE}/${id}`);
}

// ============================================================================
// Vendor API
// ============================================================================

export async function getVendors(): Promise<Vendor[]> {
  return get<Vendor[]>(`${API_BASE}/vendors`);
}

// ============================================================================
// Utility API
// ============================================================================

export async function getNextPONumber(): Promise<string> {
  const data = await get<PONumberResponse>(`${API_BASE}/next-number`);
  return data.po_number;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function formatCurrency(amount: number, currency: string = 'TWD'): string {
  const currencySymbols: Record<string, string> = {
    TWD: 'NT$',
    USD: '$',
    EUR: '€',
    JPY: '¥',
    CNY: '¥',
  };
  const symbol = currencySymbols[currency] || currency;
  return `${symbol} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function calculateItemAmount(quantity: number, unitPrice: number): number {
  return quantity * unitPrice;
}

export function calculateTotals(
  items: Array<{ quantity: number; unit_price: number }>,
  taxRate: number = 0.05
): { subtotal: number; taxAmount: number; grandTotal: number } {
  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const taxAmount = subtotal * taxRate;
  const grandTotal = subtotal + taxAmount;
  return { subtotal, taxAmount, grandTotal };
}
